import type { SnackBarConfig } from '@shared/components/Snackbar';
import { gicsTierRank } from '@shared/domain/company/gicsTierRank';
import type { Company } from '@/scenes/authentication/types/types.auth';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Input,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import CompanyLogo from '@shared/components/CompanyLogo';
import { useApi } from '@shared/services/api';
import CompanyDomainsPanel from '@shared/components/CompanyDomains/CompanyDomainsPanel';
import CoresignalCompanySearch from './CoresignalCompanySearch';
import TheOrgMappingPanel from './TheOrgMappingPanel';
import { log } from '../../utils/log';

interface GICSOption {
  code: string;
  name: string;
  tier?: string;
}

interface AdminEditCompanyProps {
  isOpen: boolean;
  setIsOpen: () => void;
  company: Company | null;
  onSave: (updatedCompany: Partial<Company>) => void;
  onDeleted?: (companyId: number) => void;
}

interface AdminEditCompanyState {
  companyName: string | undefined;
  isApproved: boolean;
  gicsCode: string;
  cik: string;
  stockTicker: string;
  url: string;
  profile: string;
  revenue: string;
  headcount: string;
  fiscalYearEndMonth: string;
  city: string;
  state: string;
  country: string;
}

/** One recorded news fetch for this company: when it ran, what it found. */
interface NewsFetchRow {
  id: number;
  fetchedAt: string;
  /** Articles that survived the identity gate and were ingested. */
  articleCount: number;
  /** Articles the search returned, before the gate. Null on rows predating it. */
  articlesFound: number | null;
  /** Articles the gate actually judged — already-ingested ones are skipped. */
  articlesJudged: number | null;
  articlesRejected: number | null;
  rejectedArticles: { reason: string; title: string }[];
  /** True when another plan's fetch this run was reused instead of searching. */
  reusedFetch: boolean;
  accountPlanId: number | null;
}

/** A concept the news search could be pointed at, as offered to an admin. */
interface NewsConceptCandidate {
  uri: string;
  label: string;
  type: string;
  articleCount: number;
  /** The name variant that found this concept, which may not be what was typed. */
  foundVia: string;
  wouldAutoResolve: boolean;
}

/** A concept currently pinned to the company. */
interface PinnedConcept {
  uri: string;
  label: string;
  type: string;
}

/** One step of the server's test search: what it did and what it found. */
interface NewsSearchTraceStep {
  stage: string;
  detail: string;
  resultCount?: number;
}

/** The outcome of a "Test Search Logic" run. */
interface NewsSearchTestResult {
  articles: { dateTime: string; source: string; title: string; url: string }[];
  coverageFlagged: boolean;
  steps: NewsSearchTraceStep[];
}

const AdminEditCompany: React.FC<AdminEditCompanyProps> = ({ isOpen, setIsOpen, company, onSave, onDeleted }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<AdminEditCompanyState>({
    companyName: undefined,
    isApproved: false,
    gicsCode: '',
    cik: '',
    stockTicker: '',
    url: '',
    profile: '',
    revenue: '',
    headcount: '',
    fiscalYearEndMonth: '',
    city: '',
    state: '',
    country: '',
  });

  const [gicsOptions, setGicsOptions] = useState<GICSOption[]>([]);
  const [isLoadingGics, setIsLoadingGics] = useState<boolean>(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState<boolean>(false);
  const [_snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();

  // Coresignal state
  const [coresignalExternalId, setCoresignalExternalId] = useState<string | null>(null);
  const [coresignalMetadata, setCoresignalMetadata] = useState<Record<string, unknown> | null>(null);
  const [coresignalInput, setCoresignalInput] = useState<string>('');
  const [isSettingCoresignalId, setIsSettingCoresignalId] = useState(false);

  // News concept state. `newsConceptUris` is null when nothing is pinned and
  // resolution is automatic; an empty array is a deliberate "no concept fits".
  const [newsConceptUris, setNewsConceptUris] = useState<string[] | null>(null);
  const [pinnedConcepts, setPinnedConcepts] = useState<PinnedConcept[]>([]);
  const [newsConceptQuery, setNewsConceptQuery] = useState<string>('');
  const [newsConceptResults, setNewsConceptResults] = useState<NewsConceptCandidate[]>([]);
  const [selectedConceptUris, setSelectedConceptUris] = useState<string[]>([]);
  const [isSearchingConcepts, setIsSearchingConcepts] = useState(false);
  const [isSavingConcept, setIsSavingConcept] = useState(false);
  const [savedKeywords, setSavedKeywords] = useState<string[]>([]);
  const [keywordsInput, setKeywordsInput] = useState<string>('');
  const [ignoredConcepts, setIgnoredConcepts] = useState<PinnedConcept[]>([]);
  const [ignoreKeywordsInput, setIgnoreKeywordsInput] = useState<string>('');
  const [manualIgnoreUri, setManualIgnoreUri] = useState<string>('');
  const [isTestingSearch, setIsTestingSearch] = useState(false);
  const [testResult, setTestResult] = useState<NewsSearchTestResult | null>(null);
  // Set when the full news search proved coverage-free and scheduled searches
  // are suppressed. Pinning a concept or clearing the flag resumes them.
  const [noCoverage, setNoCoverage] = useState<Record<string, unknown> | null>(null);
  const [isClearingNoCoverage, setIsClearingNoCoverage] = useState(false);
  // Recorded news fetches for this company over the past 30 days.
  const [newsFetches, setNewsFetches] = useState<NewsFetchRow[]>([]);

  // Danger tab state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const api = useApi();

  // Fetch GICS options only when modal opens and options are not already loaded
  useEffect(() => {
    if (!isOpen || gicsOptions.length > 0 || isLoadingGics) {
      return;
    }

    const fetchGicsOptions = async (): Promise<void> => {
      setIsLoadingGics(true);
      try {
        const response = await api.get('/gics');
        if (response && response.data) {
          setGicsOptions(response.data);
        }
      } catch (error) {
        log.error('Error fetching GICS options:', error);
        setSnackbarConfig({
          message: 'Error loading GICS options',
          open: true,
          type: 'error',
        });
      } finally {
        setIsLoadingGics(false);
      }
    };

    fetchGicsOptions();
  }, [isOpen]);

  useEffect(() => {
    if (company) {
      setFormData({
        companyName: company.companyName || '',
        isApproved: company.isApproved || false,
        gicsCode: company.gicsCode || '',
        cik: company.cik || '',
        stockTicker: company.stockTicker || '',
        url: company.url || '',
        profile: company.profile || '',
        revenue: company.revenue || '',
        headcount: company.headcount?.toString() || '',
        fiscalYearEndMonth: company.fiscalYearEndMonth?.toString() || '',
        city: company.city || '',
        state: company.state || '',
        country: company.country || '',
      });
      // Reset Coresignal state when company changes
      setCoresignalExternalId(null);
      setCoresignalMetadata(null);
      setCoresignalInput('');
      // Reset news concept state when company changes
      setNewsConceptUris(null);
      setNoCoverage(null);
      setPinnedConcepts([]);
      setNewsConceptQuery('');
      setNewsConceptResults([]);
      setSelectedConceptUris([]);
      setNewsFetches([]);
      setActiveTab(0);
    }
  }, [company]);

  // Fetch current Coresignal ID when modal opens
  useEffect(() => {
    if (!isOpen || !company?.id) return;

    const fetchCoresignalId = async (): Promise<void> => {
      try {
        const response = await api.get(`/admin/company/${company.id}/coresignal-id`);
        const data = response?.data || response;
        if (data) {
          setCoresignalExternalId(data.externalId ?? null);
          setCoresignalMetadata(data.metadata ?? null);
        }
      } catch (error) {
        log.error('Error fetching Coresignal ID:', error);
      }
    };

    fetchCoresignalId();
  }, [isOpen, company?.id]);

  // Fetch the pinned news concept when the modal opens
  useEffect(() => {
    if (!isOpen || !company?.id) return;

    const fetchNewsConcept = async (): Promise<void> => {
      try {
        const response = await api.get(`/admin/company/${company.id}/news-concept`);
        const data = response?.data || response;
        if (data) {
          setNewsConceptUris(Array.isArray(data.uris) ? data.uris : null);
          setPinnedConcepts(Array.isArray(data.concepts) ? data.concepts : []);
          setSelectedConceptUris(Array.isArray(data.uris) ? data.uris : []);
          setSavedKeywords(Array.isArray(data.keywords) ? data.keywords : []);
          setKeywordsInput(Array.isArray(data.keywords) ? data.keywords.join(', ') : '');
          setIgnoredConcepts(Array.isArray(data.ignoredConcepts) ? data.ignoredConcepts : []);
          setIgnoreKeywordsInput(Array.isArray(data.ignoreKeywords) ? data.ignoreKeywords.join(', ') : '');
          setNoCoverage(data.noCoverage ?? null);
        }
      } catch (error) {
        log.error('Error fetching news concept:', error);
      }
    };

    fetchNewsConcept();
  }, [isOpen, company?.id]);

  // Fetch the recent news-fetch history when the modal opens
  useEffect(() => {
    if (!isOpen || !company?.id) return;

    const fetchNewsFetches = async (): Promise<void> => {
      try {
        const response = await api.get(`/admin/company/${company.id}/news-fetches`);
        const data = response?.data || response;
        setNewsFetches(Array.isArray(data?.results) ? data.results : []);
      } catch (error) {
        log.error('Error fetching news fetch history:', error);
      }
    };

    fetchNewsFetches();
  }, [isOpen, company?.id]);

  const handleInputChange = (field: string, value: string | boolean): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (file) {
      const effectiveCompanyId = company?.id ?? '0';
      try {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/admin/company/${effectiveCompanyId}/logo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        setSnackbarConfig({
          message: 'Logo successfully uploaded!',
          open: true,
          type: 'success',
        });
      } catch (error) {
        log.error('Error uploading logo:', error);
        setSnackbarConfig({
          message: 'Error uploading logo',
          open: true,
          type: 'error',
        });
      }
    }
  };

  const handleGetSuggestions = async (): Promise<void> => {
    if (!company?.id) return;

    setIsLoadingSuggestions(true);
    try {
      const emptyFields: string[] = [];

      const fieldMapping: Record<string, string> = {
        cik: 'cik',
        gicsCode: 'gicsCode',
        url: 'domain',
        profile: 'description',
        revenue: 'revenue',
        headcount: 'headcount',
        fiscalYearEndMonth: 'fiscalYear',
      };

      for (const [formField, apiField] of Object.entries(fieldMapping)) {
        if (!formData[formField as keyof AdminEditCompanyState]) {
          emptyFields.push(apiField);
        }
      }

      if (!formData.city || !formData.country) {
        emptyFields.push('headquarters');
      }

      if (emptyFields.length === 0) {
        setSnackbarConfig({
          message: 'All fields are already filled',
          open: true,
          type: 'info',
        });
        return;
      }

      const response = await api.post('/ai/get-company-metadata-suggestions', {
        companyId: company.id,
        fields: emptyFields,
      });

      if (response.error) {
        throw new Error(response.message || 'Failed to get suggestions');
      }

      const suggestions = response.data || response;
      const updates: Partial<AdminEditCompanyState> = {};

      if (suggestions.cikSuggestion?.cik) {
        updates.cik = suggestions.cikSuggestion.cik;
      }

      if (suggestions.gicsCodeSuggestion?.code) {
        updates.gicsCode = suggestions.gicsCodeSuggestion.code;
      }

      if (suggestions.domainSuggestion) {
        updates.url = suggestions.domainSuggestion;
      }

      if (suggestions.revenueSuggestion !== undefined && suggestions.revenueSuggestion !== null) {
        updates.revenue = suggestions.revenueSuggestion.toString();
      }

      if (suggestions.headcountSuggestion !== undefined && suggestions.headcountSuggestion !== null) {
        updates.headcount = suggestions.headcountSuggestion.toString();
      }

      if (suggestions.fiscalYearEndMonthSuggestion !== undefined && suggestions.fiscalYearEndMonthSuggestion !== null) {
        updates.fiscalYearEndMonth = suggestions.fiscalYearEndMonthSuggestion.toString();
      }

      if (suggestions.descriptionSuggestion) {
        updates.profile = suggestions.descriptionSuggestion;
      }

      if (suggestions.headquartersSuggestion) {
        const hq = suggestions.headquartersSuggestion;
        if (hq.city) {
          updates.city = hq.city;
        }
        if (hq.state) {
          updates.state = hq.state;
        }
        if (hq.country) {
          updates.country = hq.country;
        }
      }

      setFormData((prev) => ({ ...prev, ...updates }));

      const appliedCount = Object.keys(updates).length;
      setSnackbarConfig({
        message: `Applied ${appliedCount} suggestion${appliedCount !== 1 ? 's' : ''}`,
        open: true,
        type: 'success',
      });
    } catch (error) {
      log.error('Error getting suggestions:', error);
      setSnackbarConfig({
        message: 'Error getting AI suggestions',
        open: true,
        type: 'error',
      });
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!company) return;

    const updatedCompany = {
      id: company.id,
      companyName: formData.companyName,
      isApproved: formData.isApproved,
      gicsCode: formData.gicsCode === '' ? undefined : formData.gicsCode,
      cik: formData.cik === '' ? undefined : formData.cik,
      stockTicker: formData.stockTicker === '' ? undefined : formData.stockTicker,
      url: formData.url === '' ? undefined : formData.url,
      profile: formData.profile === '' ? undefined : formData.profile,
      revenue: formData.revenue === '' ? undefined : formData.revenue,
      headcount: formData.headcount === '' ? undefined : Number.parseInt(formData.headcount),
      fiscalYearEndMonth: formData.fiscalYearEndMonth === '' ? undefined : Number.parseInt(formData.fiscalYearEndMonth),
      city: formData.city === '' ? undefined : formData.city,
      state: formData.state === '' ? undefined : formData.state,
      country: formData.country === '' ? undefined : formData.country,
    };

    onSave(updatedCompany);
  };

  // ── News concept handlers ───────────────────────────────────────────────

  const handleNewsConceptSearch = async (): Promise<void> => {
    if (!company?.id) return;

    setIsSearchingConcepts(true);
    setNewsConceptResults([]);

    try {
      // An empty query means "search the company's own name", which shows the
      // candidates automatic resolution is choosing between right now.
      const response = await api.post(`/admin/company/${company.id}/news-concept-search`, {
        query: newsConceptQuery.trim() || undefined,
      });

      const data = response?.data || response;
      if (data?.results?.length > 0) {
        setNewsConceptResults(data.results);
      } else {
        setSnackbarConfig({ message: 'No concepts found for that name', open: true, type: 'info' });
      }
    } catch (error) {
      log.error('Error searching news concepts:', error);
      setSnackbarConfig({ message: 'Error searching concepts', open: true, type: 'error' });
    } finally {
      setIsSearchingConcepts(false);
    }
  };

  const parseCsv = (value: string): string[] =>
    value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);

  /**
   * Persist the whole news config. Every save sends the complete state — the
   * server stores it as one row, so a partial payload would clobber the rest.
   */
  const putNewsConfig = async (
    config: {
      concepts: PinnedConcept[];
      ignoreKeywords: string[];
      ignoredConcepts: PinnedConcept[];
      keywords: string[];
      uris: string[] | null;
    },
    successMessage: string,
  ): Promise<void> => {
    if (!company?.id) return;

    setIsSavingConcept(true);
    try {
      const response = await api.put(`/admin/company/${company.id}/news-concept`, config);

      const data = response?.data || response;
      setNewsConceptUris(Array.isArray(data?.uris) ? data.uris : null);
      setPinnedConcepts(Array.isArray(data?.concepts) ? data.concepts : []);
      setSelectedConceptUris(Array.isArray(data?.uris) ? data.uris : []);
      setSavedKeywords(Array.isArray(data?.keywords) ? data.keywords : []);
      setKeywordsInput(Array.isArray(data?.keywords) ? data.keywords.join(', ') : '');
      setIgnoredConcepts(Array.isArray(data?.ignoredConcepts) ? data.ignoredConcepts : []);
      setIgnoreKeywordsInput(Array.isArray(data?.ignoreKeywords) ? data.ignoreKeywords.join(', ') : '');
      // Any config change clears the no-coverage flag server-side.
      setNoCoverage(null);

      setSnackbarConfig({ message: successMessage, open: true, type: 'success' });
    } catch (error) {
      log.error('Error saving news config:', error);
      setSnackbarConfig({ message: 'Error saving news configuration', open: true, type: 'error' });
    } finally {
      setIsSavingConcept(false);
    }
  };

  /** `uris` null clears both overrides and restores automatic resolution. */
  const handleSaveNewsConcept = async (uris: string[] | null): Promise<void> => {
    const concepts = uris
      ? newsConceptResults
          .filter((candidate) => uris.includes(candidate.uri))
          .map(({ uri, label, type }) => ({ label, type, uri }))
      : [];

    // Keep any already-pinned concept we have no fresh search result for, so
    // re-pinning after a page load doesn't lose its label.
    const merged = uris
      ? uris.map(
          (uri) =>
            concepts.find((concept) => concept.uri === uri) ??
            pinnedConcepts.find((concept) => concept.uri === uri) ?? { label: uri, type: 'unknown', uri },
        )
      : [];

    await putNewsConfig(
      {
        concepts: merged,
        ignoreKeywords: parseCsv(ignoreKeywordsInput),
        ignoredConcepts,
        // Concept and keyword overrides are either/or — pinning concepts (or
        // clearing) drops any keyword override.
        keywords: [],
        uris,
      },
      uris === null
        ? 'Overrides cleared — resolution is automatic again'
        : uris.length === 0
          ? 'Pinned as "no concept" — news will use keyword fallback search only'
          : `Pinned ${uris.length} concept${uris.length > 1 ? 's' : ''}`,
    );
  };

  /** Replace the automatic keyword fallbacks with an explicit OR'd search. */
  const handleSaveKeywordOverride = async (): Promise<void> => {
    const keywords = parseCsv(keywordsInput);
    if (keywords.length === 0) return;

    await putNewsConfig(
      {
        concepts: [],
        ignoreKeywords: parseCsv(ignoreKeywordsInput),
        ignoredConcepts,
        keywords,
        // A keyword override implies "no concept": the concept stage is
        // skipped and the override is the whole search.
        uris: [],
      },
      `Keyword override saved — news searches ${keywords.map((keyword) => `"${keyword}"`).join(' OR ')}`,
    );
  };

  /** The current mode fields, for saves that only touch the ignore rules. */
  const currentModeConfig = (): { concepts: PinnedConcept[]; keywords: string[]; uris: string[] | null } => ({
    concepts: pinnedConcepts,
    keywords: savedKeywords,
    uris: newsConceptUris,
  });

  const handleIgnoreConcept = async (concept: PinnedConcept): Promise<void> => {
    if (ignoredConcepts.some((ignored) => ignored.uri === concept.uri)) return;
    await putNewsConfig(
      {
        ...currentModeConfig(),
        ignoreKeywords: parseCsv(ignoreKeywordsInput),
        ignoredConcepts: [...ignoredConcepts, concept],
      },
      `Ignoring "${concept.label}"`,
    );
  };

  const handleUnignoreConcept = async (uri: string): Promise<void> => {
    await putNewsConfig(
      {
        ...currentModeConfig(),
        ignoreKeywords: parseCsv(ignoreKeywordsInput),
        ignoredConcepts: ignoredConcepts.filter((ignored) => ignored.uri !== uri),
      },
      'Concept no longer ignored',
    );
  };

  const handleAddManualIgnoreUri = async (): Promise<void> => {
    const uri = manualIgnoreUri.trim();
    if (uri === '') return;
    setManualIgnoreUri('');
    await handleIgnoreConcept({ label: uri, type: 'unknown', uri });
  };

  const handleSaveIgnoreKeywords = async (): Promise<void> => {
    await putNewsConfig(
      {
        ...currentModeConfig(),
        ignoreKeywords: parseCsv(ignoreKeywordsInput),
        ignoredConcepts,
      },
      'Ignore keywords saved',
    );
  };

  /** Run the live pipeline's search logic server-side and show its trace. */
  const handleTestSearch = async (): Promise<void> => {
    if (!company?.id) return;

    setIsTestingSearch(true);
    setTestResult(null);
    try {
      const response = await api.post(`/admin/company/${company.id}/news-search-test`, {});
      const data = response?.data || response;
      setTestResult({
        articles: Array.isArray(data?.articles) ? data.articles : [],
        coverageFlagged: data?.coverageFlagged === true,
        steps: Array.isArray(data?.steps) ? data.steps : [],
      });
    } catch (error) {
      log.error('Error running the test search:', error);
      setSnackbarConfig({ message: 'Error running the test search', open: true, type: 'error' });
    } finally {
      setIsTestingSearch(false);
    }
  };

  const toggleConceptSelection = (uri: string): void => {
    setSelectedConceptUris((prev) =>
      prev.includes(uri) ? prev.filter((selected) => selected !== uri) : [...prev, uri],
    );
  };

  const handleClearNoCoverage = async (): Promise<void> => {
    if (!company?.id) return;
    setIsClearingNoCoverage(true);
    try {
      await api.delete(`/admin/company/${company.id}/news-coverage-flag`);
      setNoCoverage(null);
      setSnackbarConfig({ message: 'Flag cleared — scheduled news searches resume', open: true, type: 'success' });
    } catch (error) {
      log.error('Error clearing the no-coverage flag:', error);
      setSnackbarConfig({ message: 'Error clearing the flag', open: true, type: 'error' });
    } finally {
      setIsClearingNoCoverage(false);
    }
  };

  // ── Coresignal handlers ─────────────────────────────────────────────────

  const handleSetCoresignalId = async (idToSet: number | null): Promise<void> => {
    if (!company?.id) return;

    setIsSettingCoresignalId(true);
    try {
      const response = await api.put(`/admin/company/${company.id}/coresignal-id`, {
        coresignalId: idToSet,
      });

      const data = response?.data || response;
      if (data) {
        setCoresignalExternalId(data.externalId ?? null);
        setCoresignalMetadata(data.metadata ?? null);
        setCoresignalInput('');
        setSnackbarConfig({
          message: idToSet ? `Coresignal ID set to ${idToSet}` : 'Coresignal ID cleared',
          open: true,
          type: 'success',
        });
      }
    } catch (error) {
      log.error('Error setting Coresignal ID:', error);
      setSnackbarConfig({ message: 'Error setting Coresignal ID', open: true, type: 'error' });
    } finally {
      setIsSettingCoresignalId(false);
    }
  };

  // ── Danger handlers ─────────────────────────────────────────────────────

  const handleDeleteCompany = async (): Promise<void> => {
    if (!company?.id) return;

    setIsDeleting(true);
    try {
      await api.delete(`/admin/company/${company.id}`);
      setIsDeleteConfirmOpen(false);
      onDeleted?.(company.id);
      setIsOpen();
    } catch (error) {
      log.error('Error deleting company:', error);
      setSnackbarConfig({ message: 'Error deleting company', open: true, type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  const renderCompanyDetailsTab = (): React.ReactNode => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          label="Company Name"
          value={formData.companyName}
          onChange={(e) => handleInputChange('companyName', e.target.value)}
          fullWidth
        />
        <Tooltip title="Get AI suggestions for empty fields">
          <IconButton onClick={handleGetSuggestions} disabled={isLoadingSuggestions || !company?.id} color="primary">
            {isLoadingSuggestions ? <CircularProgress size={20} /> : '🪄'}
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          label="CIK (SEC Central Index Key)"
          value={formData.cik}
          onChange={(e) => handleInputChange('cik', e.target.value)}
          placeholder="e.g., 0000320193"
          helperText="Used to fetch SEC filings (10-Q, 10-K)"
        />

        <TextField
          label="Stock Ticker"
          value={formData.stockTicker}
          onChange={(e) => handleInputChange('stockTicker', e.target.value)}
          placeholder="e.g., AAPL"
          helperText="Company stock ticker symbol"
        />
      </Box>

      <TextField
        label="Company URL"
        value={formData.url}
        onChange={(e) => handleInputChange('url', e.target.value)}
        fullWidth
        placeholder="https://example.com"
        helperText="Company website URL"
      />

      <TextField
        label="Company Description"
        value={formData.profile}
        onChange={(e) => handleInputChange('profile', e.target.value)}
        fullWidth
        multiline
        rows={3}
        placeholder="Brief description of what the company does"
        helperText="3-5 sentences describing the company's business"
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          label="Revenue"
          value={formData.revenue}
          onChange={(e) => handleInputChange('revenue', e.target.value)}
          placeholder="e.g., 1500000000 for $1.5B"
          helperText="Annual revenue in USD"
        />

        <TextField
          label="Headcount"
          value={formData.headcount}
          onChange={(e) => handleInputChange('headcount', e.target.value)}
          type="number"
          placeholder="e.g., 5000"
          helperText="Number of employees"
        />
      </Box>

      <TextField
        label="Fiscal Year End Month"
        value={formData.fiscalYearEndMonth}
        onChange={(e) => handleInputChange('fiscalYearEndMonth', e.target.value)}
        type="number"
        placeholder="e.g., 12 for December"
        helperText="Month number (1-12) when the fiscal year ends"
        inputProps={{ min: 1, max: 12 }}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 2 }}>
        <TextField
          label="City"
          value={formData.city}
          onChange={(e) => handleInputChange('city', e.target.value)}
          placeholder="e.g., San Francisco"
          helperText="HQ city"
        />

        <TextField
          label="State"
          value={formData.state}
          onChange={(e) => handleInputChange('state', e.target.value)}
          placeholder="e.g., California"
          helperText="HQ State/province"
        />

        <TextField
          label="Country"
          value={formData.country}
          onChange={(e) => handleInputChange('country', e.target.value)}
          placeholder="e.g., United States"
          helperText="HQ country"
        />
      </Box>

      <FormControlLabel
        control={
          <Checkbox checked={formData.isApproved} onChange={(e) => handleInputChange('isApproved', e.target.checked)} />
        }
        label="Approved"
      />

      <Autocomplete
        options={gicsOptions.sort((a, b) => {
          if (a.tier === b.tier) {
            return a.name.localeCompare(b.name);
          }
          return gicsTierRank(a.tier) - gicsTierRank(b.tier);
        })}
        loading={isLoadingGics}
        value={gicsOptions.find((option) => option.code === formData.gicsCode) || null}
        onChange={(_event, newValue) => {
          handleInputChange('gicsCode', newValue?.code || '');
        }}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        groupBy={(option) => option.tier || 'Other'}
        renderGroup={(params) => (
          <li key={params.key}>
            <Typography variant="subtitle2" sx={{ backgroundColor: '#f0f0f0', padding: '4px 8px' }}>
              {params.group}
            </Typography>
            <ul style={{ margin: 0, padding: 0 }}>{params.children}</ul>
          </li>
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.code}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                Code: {option.code}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} label="GICS Classification" fullWidth InputProps={params.InputProps as any} />
        )}
      />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Company Logo
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CompanyLogo
            companyName={company?.companyName || 'Company'}
            companyId={company?.id}
            companyUrl={company?.url ?? undefined}
            sx={{ width: 64, height: 64 }}
            variant="square"
          />
          <Input type="file" onChange={handleLogoChange} inputProps={{ 'aria-label': 'Upload logo' }} />
        </Box>
      </Box>
    </Box>
  );

  const renderCoresignalTab = (): React.ReactNode => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Typography variant="h6">Coresignal Company ID</Typography>

      {/* Current ID + stored metadata */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Current ID:
        </Typography>
        {coresignalExternalId ? (
          <>
            <Chip label={coresignalExternalId} size="small" />
            <Button
              size="small"
              color="error"
              variant="text"
              onClick={() => handleSetCoresignalId(null)}
              disabled={isSettingCoresignalId}
            >
              Clear
            </Button>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Not set
          </Typography>
        )}
      </Box>

      {coresignalExternalId && coresignalMetadata && (
        <Card variant="outlined" sx={{ backgroundColor: 'grey.50' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Stored Coresignal Data
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 0.5, columnGap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Website:
              </Typography>
              <Typography variant="body2">{(coresignalMetadata.websites_main as string) || 'N/A'}</Typography>

              <Typography variant="body2" color="text.secondary">
                LinkedIn:
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {(coresignalMetadata.websites_linkedin as string) || 'N/A'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                HQ Country:
              </Typography>
              <Typography variant="body2">{(coresignalMetadata.location_hq_country as string) || 'N/A'}</Typography>

              <Typography variant="body2" color="text.secondary">
                Description:
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {(coresignalMetadata.description as string) || 'N/A'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      <Divider />

      {/* Manual ID entry */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          label="Enter Coresignal ID manually"
          value={coresignalInput}
          onChange={(e) => setCoresignalInput(e.target.value)}
          type="number"
          size="small"
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={() => handleSetCoresignalId(Number(coresignalInput))}
          disabled={!coresignalInput || isSettingCoresignalId}
        >
          {isSettingCoresignalId ? <CircularProgress size={20} /> : 'Set ID'}
        </Button>
      </Box>

      {company?.id && (
        <CoresignalCompanySearch
          companyId={company.id}
          companyName={formData.companyName}
          companyUrl={formData.url}
          currentId={coresignalExternalId}
          isSetting={isSettingCoresignalId}
          onSelect={(coresignalId) => handleSetCoresignalId(coresignalId)}
        />
      )}
    </Box>
  );

  const renderNewsConceptCard = (candidate: NewsConceptCandidate): React.ReactNode => {
    const isSelected = selectedConceptUris.includes(candidate.uri);
    const isPinned = newsConceptUris?.includes(candidate.uri) ?? false;
    const isIgnored = ignoredConcepts.some((ignored) => ignored.uri === candidate.uri);

    return (
      <Card
        key={candidate.uri}
        variant="outlined"
        sx={{
          border: isSelected ? '2px solid' : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          backgroundColor: isPinned ? 'action.selected' : undefined,
          opacity: isIgnored ? 0.6 : undefined,
        }}
      >
        {/* A plain clickable Box rather than CardActionArea: the Ignore
            button below must not nest inside another interactive element. */}
        <Box onClick={() => toggleConceptSelection(candidate.uri)} sx={{ cursor: 'pointer' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {candidate.label || candidate.uri}{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  ({candidate.type})
                </Typography>
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {isPinned && <Chip label="Pinned" size="small" color="success" />}
                {isSelected && !isPinned && <Chip label="Selected" size="small" color="primary" />}
                {candidate.wouldAutoResolve && <Chip label="Auto picks this" size="small" color="info" />}
                {isIgnored && <Chip label="Ignored" size="small" color="warning" />}
                {!isIgnored && (
                  <Button
                    size="small"
                    color="warning"
                    disabled={isSavingConcept}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleIgnoreConcept({
                        label: candidate.label || candidate.uri,
                        type: candidate.type,
                        uri: candidate.uri,
                      });
                    }}
                  >
                    Ignore
                  </Button>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* A concept with no recent coverage is a dead end — it silently
                  starves the company, which is how several plans went dry. */}
              <Chip
                label={`${candidate.articleCount} articles (6mo)`}
                size="small"
                color={candidate.articleCount === 0 ? 'warning' : 'default'}
                variant={candidate.articleCount === 0 ? 'filled' : 'outlined'}
              />
              {/* A legal name finds nothing in EventRegistry, so the search
                  also tries the trading name — say so when that is what hit. */}
              {candidate.foundVia && candidate.foundVia !== newsConceptQuery.trim() && (
                <Chip label={`found via "${candidate.foundVia}"`} size="small" variant="outlined" />
              )}
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {candidate.uri}
              </Typography>
            </Box>
          </CardContent>
        </Box>
      </Card>
    );
  };

  const renderNewsConceptTab = (): React.ReactNode => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Typography variant="h6">News</Typography>
      <Typography variant="body2" color="text.secondary">
        How this company&apos;s news is searched. Left alone, a concept is resolved automatically from the company name
        — which picks the wrong entity when the name is generic or the company has been renamed. Pin a concept to
        settle it, or set a keyword override when no concept fits; ignore rules exclude known impostors either way.
      </Typography>

      {noCoverage && (
        <Alert
          severity="warning"
          action={
            <Button size="small" color="inherit" onClick={handleClearNoCoverage} disabled={isClearingNoCoverage}>
              {isClearingNoCoverage ? <CircularProgress size={16} /> : 'Clear flag'}
            </Button>
          }
        >
          Flagged as having no news coverage
          {typeof noCoverage.emptyRunCount === 'number' && ` after ${noCoverage.emptyRunCount} empty search(es)`}
          {typeof noCoverage.lastCheckedAt === 'string' &&
            ` — last checked ${new Date(noCoverage.lastCheckedAt).toLocaleDateString()}`}
          . Scheduled news searches are paused apart from a periodic recheck; pinning a concept or clearing the flag
          resumes them.
        </Alert>
      )}

      {/* Current state */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Current:
        </Typography>
        {savedKeywords.length > 0 ? (
          <Chip
            label={`Keyword override: ${savedKeywords.map((keyword) => `"${keyword}"`).join(' OR ')}`}
            size="small"
            color="success"
          />
        ) : newsConceptUris === null ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            Automatic (nothing pinned)
          </Typography>
        ) : newsConceptUris.length === 0 ? (
          <Chip label="No concept — keyword search only" size="small" color="warning" />
        ) : (
          <>
            {newsConceptUris.map((uri) => (
              <Chip
                key={uri}
                label={pinnedConcepts.find((concept) => concept.uri === uri)?.label ?? uri}
                size="small"
                color="success"
              />
            ))}
          </>
        )}
        {(newsConceptUris !== null || savedKeywords.length > 0) && (
          <Button
            size="small"
            color="error"
            variant="text"
            onClick={() => handleSaveNewsConcept(null)}
            disabled={isSavingConcept}
          >
            Clear override
          </Button>
        )}
      </Box>

      <Divider />

      {/* Search */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          label="Search concepts (defaults to the company name)"
          value={newsConceptQuery}
          onChange={(e) => setNewsConceptQuery(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleNewsConceptSearch();
          }}
        />
        <Button variant="outlined" onClick={handleNewsConceptSearch} disabled={isSearchingConcepts || !company?.id}>
          {isSearchingConcepts ? <CircularProgress size={20} /> : 'Search Concepts'}
        </Button>
      </Box>

      {/* Results */}
      {newsConceptResults.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle2">
            Candidates ({newsConceptResults.length}){' — '}
            select one or more; a renamed company usually needs both its old and new concept
          </Typography>

          {newsConceptResults.map((candidate) => renderNewsConceptCard(candidate))}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <Button variant="outlined" color="warning" onClick={() => handleSaveNewsConcept([])} disabled={isSavingConcept}>
          {isSavingConcept ? <CircularProgress size={20} /> : 'Pin as "no concept"'}
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSaveNewsConcept(selectedConceptUris)}
          disabled={selectedConceptUris.length === 0 || isSavingConcept}
        >
          {isSavingConcept ? <CircularProgress size={20} /> : 'Pin Selected'}
        </Button>
      </Box>

      <Divider />

      {/* Keyword override */}
      <Typography variant="subtitle2">Keyword override</Typography>
      <Typography variant="body2" color="text.secondary">
        When no concept fits, search these terms instead (OR&apos;d, comma separated). This replaces the automatic
        keyword fallbacks entirely and skips the concept stage — pair it with ignore rules to keep impostors out.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          label='Keywords, e.g. "AG1, Athletic Greens"'
          value={keywordsInput}
          onChange={(e) => setKeywordsInput(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={handleSaveKeywordOverride}
          disabled={isSavingConcept || parseCsv(keywordsInput).length === 0}
        >
          {isSavingConcept ? <CircularProgress size={20} /> : 'Use Keyword Search'}
        </Button>
      </Box>

      <Divider />

      {/* Ignore rules */}
      <Typography variant="subtitle2">Ignore rules</Typography>
      <Typography variant="body2" color="text.secondary">
        Ignored concepts are dropped from automatic resolution and excluded from every search; ignore keywords exclude
        any article containing them. Use the concept search above and press Ignore on a candidate, or add a concept URI
        by hand.
      </Typography>
      {ignoredConcepts.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {ignoredConcepts.map((concept) => (
            <Chip
              key={concept.uri}
              label={concept.label}
              size="small"
              color="warning"
              onDelete={() => handleUnignoreConcept(concept.uri)}
              disabled={isSavingConcept}
            />
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          label="Ignore concept URI, e.g. http://en.wikipedia.org/wiki/Hispasat_AG1"
          value={manualIgnoreUri}
          onChange={(e) => setManualIgnoreUri(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAddManualIgnoreUri();
          }}
        />
        <Button
          variant="outlined"
          onClick={handleAddManualIgnoreUri}
          disabled={isSavingConcept || manualIgnoreUri.trim() === ''}
        >
          Add
        </Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          label='Ignore keywords (comma separated), e.g. "Hispasat, satellite"'
          value={ignoreKeywordsInput}
          onChange={(e) => setIgnoreKeywordsInput(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
        />
        <Button variant="outlined" onClick={handleSaveIgnoreKeywords} disabled={isSavingConcept}>
          {isSavingConcept ? <CircularProgress size={20} /> : 'Save Ignore Keywords'}
        </Button>
      </Box>

      <Divider />

      {/* Test search */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Test search
        </Typography>
        <Button variant="contained" onClick={handleTestSearch} disabled={isTestingSearch || !company?.id}>
          {isTestingSearch ? <CircularProgress size={20} /> : 'Test Search Logic'}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Runs the signal pipeline&apos;s search logic with the configuration above — which concepts were used, which
        fallbacks ran, and what each found. Nothing is ingested and no usage is recorded.
      </Typography>
      {testResult && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Stage</TableCell>
                <TableCell>What happened</TableCell>
                <TableCell align="right">Results</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testResult.steps.map((step, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    <Chip label={step.stage} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{step.detail}</TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                    {step.resultCount ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {testResult.articles.length > 0 && (
            <>
              <Typography variant="subtitle2">Articles found ({testResult.articles.length})</Typography>
              {testResult.articles.map((article) => (
                <Typography key={article.url} variant="body2">
                  <a href={article.url} target="_blank" rel="noreferrer">
                    {article.title}
                  </a>{' '}
                  <Typography component="span" variant="body2" color="text.secondary">
                    — {article.source}, {article.dateTime ? new Date(article.dateTime).toLocaleDateString() : 'no date'}
                  </Typography>
                </Typography>
              ))}
            </>
          )}
        </Box>
      )}

      <Divider />

      {/* Fetch history */}
      <Typography variant="subtitle2">Fetches (last 30 days)</Typography>
      <Typography variant="caption" color="text.secondary">
        Found = articles the search returned · Judged = new ones the identity gate examined (already-ingested
        articles are skipped) · Rejected = judged not to be about this company (hover for reasons) · Kept =
        ingested.
      </Typography>
      {newsFetches.length === 0 ? (
        <Typography variant="body2" sx={{ fontStyle: 'italic' }} color="text.secondary">
          No news fetches recorded for this company in the last 30 days.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fetched</TableCell>
              <TableCell align="right">Found</TableCell>
              <TableCell align="right">Judged</TableCell>
              <TableCell align="right">Rejected</TableCell>
              <TableCell align="right">Kept</TableCell>
              <TableCell align="right">Account plan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {newsFetches.map((fetch) => (
              <TableRow key={fetch.id}>
                <TableCell>
                  {new Date(fetch.fetchedAt).toLocaleString()}
                  {fetch.reusedFetch && (
                    <Chip
                      size="small"
                      label="reused"
                      sx={{ ml: 1 }}
                      title="Another plan on this company searched this run — these articles were reused, not searched for again."
                    />
                  )}
                </TableCell>
                <TableCell align="right">{fetch.articlesFound ?? '—'}</TableCell>
                <TableCell align="right">{fetch.articlesJudged ?? '—'}</TableCell>
                <TableCell
                  align="right"
                  // The reasons are the point of the column — a run that found
                  // plenty and kept none is a gate problem, not a search one.
                  title={fetch.rejectedArticles.map((a) => `${a.title} — ${a.reason}`).join('\n')}
                  sx={{
                    color: (fetch.articlesRejected ?? 0) > 0 ? 'error.main' : undefined,
                    cursor: fetch.rejectedArticles.length > 0 ? 'help' : undefined,
                  }}
                >
                  {fetch.articlesRejected ?? '—'}
                </TableCell>
                <TableCell align="right">{fetch.articleCount}</TableCell>
                <TableCell align="right">{fetch.accountPlanId ? `#${fetch.accountPlanId}` : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );

  const renderDangerTab = (): React.ReactNode => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
      <Typography variant="h6" color="error">
        Danger Zone
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Deleting a company is a soft delete — the record is hidden but not permanently removed.
      </Typography>
      <Box>
        <Button
          variant="outlined"
          color="error"
          onClick={() => setIsDeleteConfirmOpen(true)}
          disabled={isDeleting || !company?.id}
        >
          Delete Company
        </Button>
      </Box>
    </Box>
  );

  return (
    <Dialog open={isOpen} onClose={setIsOpen} maxWidth="lg" fullWidth>
      <DialogTitle>Edit Company</DialogTitle>

      <Tabs
        value={activeTab}
        onChange={(_e, newValue) => setActiveTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
        TabIndicatorProps={{
          sx: { backgroundColor: activeTab === 5 ? 'error.main' : undefined },
        }}
      >
        <Tab label="Company Details" />
        <Tab label="Domains" disabled={!company?.id} />
        <Tab label="Coresignal" disabled={!company?.id} />
        <Tab label="TheOrg" disabled={!company?.id} />
        <Tab label="News" disabled={!company?.id} />
        <Tab
          label="Danger"
          disabled={!company?.id}
          sx={{
            marginLeft: 'auto',
            color: 'error.main',
            '&.Mui-selected': { color: 'error.main' },
          }}
        />
      </Tabs>

      <DialogContent sx={{ minHeight: 400 }}>
        {activeTab === 0 && renderCompanyDetailsTab()}
        {activeTab === 1 && company?.id && (
          <Box sx={{ mt: 1 }}>
            <CompanyDomainsPanel companyId={company.id} />
          </Box>
        )}
        {activeTab === 2 && renderCoresignalTab()}
        {activeTab === 3 && company?.id && (
          <TheOrgMappingPanel companyId={company.id} companyUrl={formData.url || undefined} />
        )}
        {activeTab === 4 && renderNewsConceptTab()}
        {activeTab === 5 && renderDangerTab()}
      </DialogContent>

      <DialogActions>
        <Button onClick={setIsOpen}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save Changes
        </Button>
      </DialogActions>

      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => !isDeleting && setIsDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Company?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{company?.companyName || 'this company'}</strong>? This will
            soft-delete the company record.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteConfirmOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteCompany} disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AdminEditCompany;
